(ns nosco.richtext.editor
  "ProseMirror based rich text editor.

  ProseMirror is based on contenteditable but it has a functional approach
  where an internal state is processed via transactions. It's extremely
  pluggable, to the extent that even basic functionality (e.g. pressing enter)
  is provided via plugins.

  Prosemirror needs a 'schema' which is the structure of the edited document.
  A user cannot create an invalid document, and when editing an existing HTML
  document, it will be converted according to the schema, which means that
  information might be lost! Extra care has to be taken when editing existing
  redactor-created documents, we need to make sure that our schema is
  comprehensive before doing that.

  As a bootstrap, we use prosemirror-example-setup to bootstrap the basic
  functionality, but eventually we should probably copy what we need into
  our own code for extra control.

  See https://docs.google.com/presentation/d/1WVfLHCEYf5-vfnJqj-CsaxjaVPzGu8JTyYpwmg-C7bs/edit?usp=sharing

  "
  (:require
   ["nosco-prosemirror/list_commands" :as list-commands]
   ["nosco-prosemirror/placeholder_text" :refer [textPlaceholderPluginKey
                                                 textPlaceholderPluginSpec]]
   ["nosco-prosemirror/utils" :as nosco-prosemirror-utils]
   ["prosemirror-commands" :as prosemirror-commands]
   ["prosemirror-dropcursor" :as prosemirror-dropcursor]
   ["prosemirror-example-setup" :as example-setup]
   ["prosemirror-gapcursor" :as prosemirror-gapcursor]
   ["prosemirror-history" :as prosemirror-history]
   ["prosemirror-keymap" :as prosemirror-keymap]
   ["prosemirror-model" :refer [DOMParser DOMSerializer Slice]]
   ["prosemirror-state" :as prosemirror-state :refer [EditorState Plugin
                                                      PluginKey]]
   ["prosemirror-view" :refer [Decoration DecorationSet EditorView]]
   ["react" :as React]
   ["react-useportal" :default usePortal]
   [cljs-bean.core :as bean]
   [clojure.string :as string]
   [goog.object]
   [hx.hooks :as hooks :refer [useLayoutEffect useState]]
   [hx.react :as hx :refer [defnc]]
   [nosco.graphql-client]
   [nosco.react]
   [nosco.richtext.ai :as richtext-ai]
   [nosco.richtext.api :as api]
   [nosco.richtext.compat :as compat]
   [nosco.richtext.markdown :as richtext-markdown]
   [nosco.richtext.mentions :as mentions]
   [nosco.richtext.merge-tags :as merge-tags]
   [nosco.richtext.plugins.drop :as plugins-drop]
   [nosco.richtext.plugins.embedded-images :as plugins-embedded-images]
   [nosco.richtext.plugins.placeholder :as plugins-placeholder]
   [nosco.richtext.schema :as schema]
   [nosco.richtext.toolbar :as toolbar]
   [nosco.richtext.uploads :as uploads]
   [nosco.richtext.urls :as urls]
   [nosco.ui.base :as base]
   [nosco.ui.popup :as popup]
   [nosco.util :as util]))

(defn fragment->str
  "Convert a DocumentFragment into a string"
  [fragment]
  (let [p (.createElement js/document "div")]
    (.appendChild p fragment)
    (.-innerHTML p)))

(defn empty-paragraph? [node]
  (and (instance? js/HTMLElement node)
       (= "P" (.-tagName node))
       (or (not (.hasChildNodes node))
           (string/blank? (.-textContent node)))))

(defn replace-children-with-br [node]
  (set! (.-innerHTML node) "<br data-origin='nosco-pm-empty-fix'>"))

(defn insert-br-into-empty-paragraphs
  "Insert a <br> into empty paragraphs. Empty paragraphs are used to insert spacing
   between paragraphs, but PM doesn't like them during editing, so we insert them
   at serialisation time."
  [fragment]
  (let [fix (fn fix [node]
              (cond
                (empty-paragraph? node)
                (replace-children-with-br node)
                (.hasChildNodes node)
                (.forEach (.-childNodes node) fix)))]
    (fix fragment)
    fragment))

(defn doc->fragment
  "Serialize a prosemirror doc into a DocumentFragment"
  [prosemirror-doc]
  (let [serializer (-> DOMSerializer
                       (.fromSchema (schema/nosco-schema)))]
    (->
     (.serializeFragment serializer (.-content prosemirror-doc))
     (insert-br-into-empty-paragraphs))))

;; plugin keys are used to uniquely identify plugins within a
;; PM editor instance

(defonce unique-editor-key (PluginKey. "nosco-unique-key-plugin"))
(defn unique-editor-key-plugin-spec
  "A plugin that generates a unique key when it's created.
  Useful to force a re-render of any React children that might have still
  references to the previous editor."
  []
  #js {:key unique-editor-key
       :state #js {:init (fn [] #js {:editor-key (str (random-uuid))})
                   :apply (fn [_tr plugin-state]
                            ;; the key is stable throughout the lifecycle of the state
                            plugin-state)}})

(defn get-editor-unique-key [state]
  (goog.object/get (.getState unique-editor-key state) "editor-key"))

(defonce editable-plugin-key (PluginKey. "nosco-editable-plugin"))
(defn editable-plugin-spec
  "A plugin that can make the editor editable or not."
  []
  #js {:key editable-plugin-key
       :state #js {;; the state this plugin keeps is a map with only one key
                   :init (fn [] #js {:editable true})
                   ;; tr is a Transaction, plugin-state is the plugin state which
                   ;; is exactly what is declared above.
                   :apply (fn [^js tr value]
                            (this-as
                             ;; `this` is bound to the plugin
                             this
                              ;; plugins can add metadata to the transactions,
                              ;; that do not directly edit the editor state but
                              ;; can be picked up like so. See `.setMeta` below.
                              (if-let [meta (.getMeta tr this)]
                                ;; return the new state
                                #js {:editable (.-editable meta)}
                                ;; return the old state
                                value)))}
       :props #js {;; this prop determines whether the editor is editable or not
                   :editable
                   (fn [editor-state]
                     ;; use the key to locate the plugin state from the editor
                     (let [plugin-state (.getState editable-plugin-key editor-state)]
                       ;; return true/false
                       (.-editable plugin-state)))}})

(defonce max-length-plugin-key (PluginKey. "nosco-max-length-plugin"))

;; Add a decoration to the overflowing chars when the number of chars is > to max-length
;; Note that the decoration use a "from" position, which is different than a number of chars
;; Non-text nodes may make the position to increase more than the number of chars
;; We use "textBetween" to count the number of chars up to a specific position
(defn max-length-plugin-spec []
  #js {:key max-length-plugin-key
       :state #js {:init (fn [] #js {:maxLength nil})
                   :apply (fn [^js tr value]
                            (this-as this
                              ;; plugins can add metadata to the transactions,
                              ;; that do not directly edit the editor state but
                              ;; can be picked up like so. See `.setMeta` below.

                              ;; If meta are set, then set the maxLength. Note that maxLength can be nil
                              (when-let [meta (.getMeta tr this)]
                                (let [max-length (.-maxLength meta)]
                                  (goog.object/set value "maxLength" max-length)))
                              ;; Compute the decoration "from" position
                              (if-let [max-length (.-maxLength value)]
                                (let [doc (.-doc tr)
                                      text-content (.-textContent doc)]
                                  (when (> (util/char-count text-content) max-length)
                                    (loop [decoration-from (inc max-length)]
                                      ;; Check the number of chars contained between 0 and decoration-from
                                      ;; If it is < to max-length, then increase decoration-from
                                      (let [text-count (util/char-count (.textBetween doc 0 decoration-from))]
                                        (if (>= text-count max-length)
                                          (goog.object/set value "decorationFrom" decoration-from)
                                          (recur (+ decoration-from (- max-length text-count))))))))
                                (goog.object/set value "decorationFrom" nil))
                              value))}
       ;; Returns a DecorationSet that colors the text overflowing max-length
       :props #js {:decorations
                   (fn [editor-state]
                     (let [doc (.-doc editor-state)
                           plugin-state (.getState max-length-plugin-key editor-state)
                           decoration-from (.-decorationFrom plugin-state)
                           decoration-set (if decoration-from
                                            (let [decoration ((.-inline Decoration)
                                                              decoration-from ##Inf
                                                              #js {:class "text-red"})]
                                              (.add (.-empty DecorationSet) doc #js [decoration]))
                                            (.-empty DecorationSet))]
                       decoration-set))}})

(defn set-editor-placeholder-text [view placeholder-text]
  (let [state (.-state view)
        ;; note, .get and .getState are not the same, .get returns the actual plugin
        plugin (.get textPlaceholderPluginKey state)
        ;; start a new transaction - .-tr is backed by a getter
        ^js tr (-> view .-state .-tr)]
    ;; the transaction is empty, as it doesn't affect the document state,
    ;; but it has metadata that gets picked up by the plugin. The
    ;; metadata is scoped to this plugin only
    (.setMeta tr plugin #js {:text placeholder-text})
    ;; dispatch the transaction which will kick off a whole editor refresh cycle
    (.dispatch view tr)))

(defn set-editor-editable
  "Make the editor editable or not."
  [view editable]
  (let [state (.-state view)
        ;; note, .get and .getState are not the same, .get returns the actual plugin
        plugin (.get editable-plugin-key state)
        ;; start a new transaction - .-tr is backed by a getter
        ^js tr (-> view .-state .-tr)]
    ;; the transaction is empty, as it doesn't affect the document state,
    ;; but it has metadata that gets picked up by the plugin. The
    ;; metadata is scoped to this plugin only
    (.setMeta tr plugin #js {:editable editable})
    ;; dispatch the transaction which will kick off a whole editor refresh cycle
    (.dispatch view tr)))

(defn set-editor-max-length
  [view max-length]
  (let [state (.-state view)
        ;; note, .get and .getState are not the same, .get returns the actual plugin
        plugin (.get max-length-plugin-key state)
        ;; start a new transaction - .-tr is backed by a getter
        ^js tr (-> view .-state .-tr)]
    ;; the transaction is empty, as it doesn't affect the document state,
    ;; but it has metadata that gets picked up by the plugin. The
    ;; metadata is scoped to this plugin only
    (.setMeta tr plugin #js {:maxLength max-length})
    ;; dispatch the transaction which will kick off a whole editor refresh cycle
    (.dispatch view tr)))

;; in principle, we might want to have different schemas
;; but for now, let's keep it simple and use one schema
;; everywhere. This needs to be stable throughout the lifecycle
;; of a PM instance so be careful when creating it within
;; a React component
(defonce schema (schema/nosco-schema))

;; Note about XSS here:
;; Prosemirror's schemas are essentially a great big whitelist
;; Only the allowed tags and the allowed attributes ever make it into the DOM
;; So we don't need to run it through dompurify at this point.
(defn html->doc
  ([html] (html->doc html schema))
  ([html schema]
   (let [dom-node (-> (js/DOMParser.)
                      (.parseFromString html "text/html"))
         dom-node (compat/preprocess dom-node)
         ;; parse the DOM node into a PM document according to the schema
         parser (-> DOMParser
                    (.fromSchema schema))
         ;; parse the HTML into a DOM node
         doc (.parse parser dom-node)]
     doc)))

(defn set-editor-html [view html]
  (let [doc (html->doc html)
        tr (-> view .-state .-tr)]
    (.replace tr 0
              (goog.object/getValueByKeys view "state" "doc" "content" "size")
              (Slice. (.-content doc) 0 0))
    (.dispatch view tr)))

(defn backspace [state dispatch view]
  (cond
    ;; if we're at the start of a list item, we need to either backspace
    ;;directly to an empty list item above, or outdent this node
    (and (list-commands/isInsideListItem state)
         (nosco-prosemirror-utils/isEmptySelectionAtStart state)
         (nosco-prosemirror-utils/isFirstChildOfParent state)
         (list-commands/canOutdent state))
    (let [cmd (prosemirror-commands/chainCommands
               (list-commands/deletePreviousEmptyListItem)
               (list-commands/outdentList))]
      (cmd state dispatch view))
    ;; if we're just inside a paragraph node (or gapcursor is shown) and backspace, then try to join
    ;; the text to the previous list item, if one exists
    (and (nosco-prosemirror-utils/isEmptySelectionAtStart state)
         (list-commands/canToJoinToPreviousListItem state))
    (let [cmd (list-commands/joinToPreviousListItem)]
      (cmd state dispatch view))
    :else false))

(defn make-keymap-plugin
  "Returns a Plugin which dispatches functions when keybindings are triggered."
  [{:keys [on-link-editor-modal-open]}]
  (prosemirror-keymap/keymap
   ;; `Mod` is a shorthand for Cmd on macOS and Ctrl on other platforms
   #js {"Mod-k" (fn [] (on-link-editor-modal-open) true)
        "Backspace" backspace
        "Mod-Backspace" backspace,
        "Shift-Backspace" backspace}))

(defn basic-plugins [schema]
  #js [(example-setup/buildInputRules schema)
       (prosemirror-keymap/keymap (example-setup/buildKeymap schema #js {}))
       (prosemirror-keymap/keymap prosemirror-commands/baseKeymap)
       (prosemirror-dropcursor/dropCursor)
       (prosemirror-gapcursor/gapCursor)
       (prosemirror-history/history)])

(defn setup-plugins
  "Given a PM schema and some options, return a JS array of plugins"
  [schema {:keys [idea-id on-link-editor-modal-open modal-manager-ref overlay-container-node]}]
  (let [plugins (basic-plugins schema)
        autocomplete-plugins (mentions/plugins idea-id)
        nosco-plugins #js [(plugins-drop/drop-plugin)
                           (urls/make-pasted-urls-links-plugin (.. schema -marks -link))
                           (urls/handle-pasted-urls-selection-plugin)
                           (plugins-embedded-images/convert-embedded-images-plugin)
                           (merge-tags/merge-tags-plugin)
                           (uploads/controlled-figure-plugin {:modal-manager-ref modal-manager-ref
                                                              :overlay-container-node overlay-container-node})
                           richtext-ai/persistent-selection-plugin
                           (Plugin. (textPlaceholderPluginSpec))
                           (Plugin. (editable-plugin-spec))
                           (Plugin. (max-length-plugin-spec))
                           (Plugin. (unique-editor-key-plugin-spec))
                           (plugins-placeholder/placeholder-plugin)
                           (make-keymap-plugin {:on-link-editor-modal-open on-link-editor-modal-open})]]
    ;; the order of plugins is important, when we want to do custom key handling
    ;; in our case, the user mentions plugins needs to handle "Enter" being pressed,
    ;; which is otherwise handled in the base keymap of the exampleSetup
    (->> plugins
         (.concat autocomplete-plugins)
         (.concat nosco-plugins))))

(defn formula-plugins [formula-refs]
  (->> #js [(prosemirror-keymap/keymap (example-setup/buildKeymap schema #js {}))
            (prosemirror-keymap/keymap prosemirror-commands/baseKeymap)
            (prosemirror-history/history)]
       (.concat (mentions/formula-plugins formula-refs))
       (.concat #js [(Plugin. (textPlaceholderPluginSpec))
                     (Plugin. (editable-plugin-spec))
                     (Plugin. (max-length-plugin-spec))
                     (Plugin. (unique-editor-key-plugin-spec))
                     (plugins-placeholder/placeholder-plugin)])))

(defn create-editor-state
  "Given a schema, an initial html and some plugins, create an initial EditorState"
  [schema html plugins]
  (let [doc (html->doc html schema)]
    (.create EditorState #js {:doc doc
                              :plugins plugins})))

(defnc HTMLEditorImpl
  "Implentation of the HTMLEditorImpl.
  Relies on the parent of this to be memoized so that it doesn't re-render unecessarily.
  It manages a DOM node that contains the prosemirror contenteditable, and also managing
  the prosemirror EditorView that needs to stay stable throughout the lifecycle of this
  component.

  the various on-* function arguments are one-shot: changing them after the initial mount
  has no effect. To change them on the fly, you need to destroy this component and create
  a new one, via using a different react `key`.

  initial-html, editable can be used to modify the editor as expected.
  Note that initial-html is only set during editor init, changing it after the fact will
  not update the editor (ProseMirror likes to keep its own state).

  if a ref is passed in, its .-current value will
  contain an implementation of nosco.richtext.api/HTMLEditorAPI
  "
  [{:keys [initial-html ;; initial html to edit, doesn't rerender when changed
           on-create ;; optional, no-args function called when the instance is created
           on-change ;; optional, function of [html]
           on-keydown ;; optional, function of [event], called on every keydown in the editor
           on-blur ;; optional, function of [event], called on blur
           init-focus ;; if true, focus the editor immediately on mount
           editable ;; if true, editor is editable, otherwise read-only
           max-length ;; The maximum number of chars allowed, or nil
           wrapper-class ;; class of the wrapper div
           wrapper-style ;; style of the wrapper div
           class ;; class of the PM div
           style ;; style of the PM div
           name ;; if provider, maintain a hidden input with the contents of the editor
           error
           placeholder
           toolbar-layout
           toolbar-layout-bottom
           submit-render ;; A function that render submit/cancel/save buttons on the right side of the bottom toolbar
           no-border
           ;; Formula-refs used by the submission form "calculated number" fields. When provided, the
           ;; plugins are overriden by the "formula-plugins"
           formula-refs
           before-editor ;; a React element to place between the toolbar and editor

           ;; Optional context for some plugins
           idea-id
           ai-context]
    :or {initial-html ""
         placeholder ""
         toolbar-layout [:text-size :text-style
                         :paragraph-style :links :images
                         :files :videos]}}
   ref]
  ;; the generall appraoch is taken from https://github.com/dminkovsky/use-prosemirror/blob/main/src/ProseMirror.tsx
  ;; but doing it in here allows for much more flexibility and control.
  (let [root-el-ref (React/useRef)
        html-input-ref (React/useRef)
        [link-editor-modal-open ->link-editor-modal-open] (useState false)
        ;; TODO re the conversion of the AI coordinates
        ;; this is definitely brittle, we should be relying on popper.js
        ;; to do this kind of conversion for us
        ;; things like the user mentions popup use popper and have had no issues
        ;; however, they exhibit some lag on scroll
        ;; we should investigate using html popovers. It seems like Floating UI,
        ;; which is popper's successor, adds support for those
        convert-ai-top (richtext-ai/use-convert-top)
        [ai-state ai-dispatch] (richtext-ai/use-ai-state)
        ;; lazily create the initial state, to avoid calling this on every render
        ai-toolbar-button-ref (React/useRef)
        modal-manager-ref (hooks/useContext base/ModalManagerContext)
        overlay-container-node (hooks/useContext popup/OverlayContainerContext)
        create-initial-state (fn [html]
                               (create-editor-state
                                schema
                                html
                                (if (some? formula-refs)
                                  (formula-plugins formula-refs)
                                  (setup-plugins schema {:idea-id idea-id
                                                         :on-link-editor-modal-open #(->link-editor-modal-open true)
                                                         :modal-manager-ref modal-manager-ref
                                                         :overlay-container-node overlay-container-node}))))
        [state set-state] (useState (partial create-initial-state initial-html))
        ;; if on-change is provided, used to determine whether
        ;; the produced html changed after a transaction
        prev-html-ref (React/useRef)
        ;; will contain an EditorView instance
        view-ref (React/useRef)
        wrapper-ref (React/useRef)
        ;; will contain a (created only once) implementation of HTMLEditorAPI
        [api-impl] (useState (fn [] (reify api/HTMLEditorAPI
                                      (get-view [_]
                                        (.-current view-ref))
                                      (get-state [this]
                                        (.-state (api/get-view this)))
                                      (get-char-count [this]
                                        (-> (api/get-state this)
                                            (.-doc)
                                            (.-textContent)
                                            (util/char-count)))
                                      (set-html [this html]
                                        (set-editor-html (api/get-view this) html))
                                      (get-html [this]
                                        (-> (api/get-state this)
                                            .-doc
                                            doc->fragment
                                            fragment->str))
                                      (reset [this]
                                        (let [new-state (create-initial-state initial-html)
                                              ^js view (api/get-view this)]
                                          (.updateState view new-state)
                                          (set-state new-state)))
                                      (dispatch [this tr]
                                        ((.-dispatch (api/get-view this)) tr)))))]
    (hooks/useEffect (fn []
                       (ai-dispatch (richtext-ai/convert-top-action convert-ai-top)))
                     [convert-ai-top])
    ;; customise what the ref is. Convenience so that the caller deals
    ;; with a smaller surface API.
    (React/useImperativeHandle ref (fn [] api-impl))
    ;; on mount, immediately create the PM view
    (useLayoutEffect
     (fn []
       (let [editor-view
             (EditorView.
              #js {:mount (.-current root-el-ref)}
              #js {:handleKeyDown (fn [_ event]
                                    (when on-keydown
                                      (on-keydown event)))
                   ;; the initial state; this code only runs on mount
                   :state state
                   :dispatchTransaction
                   (fn [transaction]
                     ;; get the new state
                     (let [new-state (-> view-ref
                                         .-current
                                         .-state
                                         (.apply transaction))
                           ;; apply it to the view
                           _ (-> view-ref .-current (.updateState new-state))
                           ;; there seems to be something after .updateState that is mutating the state
                           ;; so we pluck it back out from the view to call set-state
                           ;; the invariant being (identical? (.-state view) new-state)
                           new-state (-> view-ref .-current .-state)]
                       ;; re-render the component so that toolbars etc refresh
                       (set-state new-state)
                       ;; callback for outside callers
                       (when (or name on-change)
                         (let [prev-html (.-current prev-html-ref)
                               new-html (-> new-state
                                            .-doc
                                            doc->fragment
                                            fragment->str)]
                           (when-not (= prev-html new-html)
                             (set! (.-current prev-html-ref) new-html)
                             (when on-change
                               (on-change new-html))
                             (when name
                               (.setAttribute (.-current html-input-ref) "value" new-html)))))))})]
         (set! (.-current view-ref) editor-view)
         (when-not (.-current prev-html-ref)
           (set! (.-current prev-html-ref)
                 (-> state
                     .-doc
                     doc->fragment
                     fragment->str)))
         (when on-create
           (on-create))
         (when init-focus
           (.focus editor-view))
         ;; destroy when unmounted
         (fn []
           (.destroy editor-view))))
     ;; TODO as-is, changing the `on-change`` prop will
     ;; do nothing -- as the useLayoutEffect is not called again when it changes.
     ;; Perhaps we could do that, but we have to be careful to maintain the editor state
     ;; (which is possible, as we have it in `state`).
     [])
    ;; Update the `on-keydown` prop
    (hooks/useEffect
     (fn []
       (when-let [editor (.-current view-ref)]
         (.setProps editor #js {:handleKeyDown (fn [_ event]
                                                 (when on-keydown
                                                   (on-keydown event)))})))
     [on-keydown])
    (useLayoutEffect
     (fn []
       (set-editor-editable (.-current view-ref) editable))
     [editable])
    (useLayoutEffect
     (fn []
       (set-editor-max-length (.-current view-ref) max-length))
     [])
    (useLayoutEffect
     (fn []
       (set-editor-placeholder-text (.-current view-ref) (str placeholder))
       js/undefined)
     [placeholder])
    (let [view (-> view-ref .-current)
          container-ref (hx.hooks/useContext popup/OverlayContainerContext)]
      [:div {:ref wrapper-ref
             :style wrapper-style
             :class ["nosco-richtext-editor"
                     wrapper-class
                     (when error "nosco-richtext-editor--error")
                     (when no-border "nosco-richtext-editor--no-border")
                     (when (richtext-ai/ai-suggestions-open? ai-state) "nosco-richtext-editor--ai-suggestions-open")]
             :onBlur (when on-blur
                       (fn [e] (let [current-target (.-currentTarget e)]
                                 (js/requestAnimationFrame
                                  (fn []
                                    (when (and current-target
                                               (not (.contains current-target js/document.activeElement)))
                                      (on-blur e)))))))}
       [richtext-ai/AISuggestions
        {:state ai-state
         :dispatch ai-dispatch
         :editor-api api-impl
         :ai-toolbar-button-ref ai-toolbar-button-ref}]
       (when name
         [:input {:type "hidden"
                  :ref html-input-ref
                  :name name}])
       ;; on first render, set the value of the hidden input
       ;; after that, it it set imperatively via ProseMirror's transactions
       ;; can't use default-value as it clobbers the current value when re-rendering
       (hooks/useEffect
        (fn []
          (when name
            (.setAttribute (.-current html-input-ref) "value" (str initial-html))))
        [])
       (when-not (empty? toolbar-layout)
         [toolbar/HTMLEditorToolbar
          {:schema schema
           :view view
           :state state
           :layout toolbar-layout
           :formula-refs formula-refs
           :variant (if no-border :white :gray)
           :link-editor-modal-open link-editor-modal-open
           :on-link-editor-modal-open #(->link-editor-modal-open true)
           :on-link-editor-modal-close #(->link-editor-modal-open false)

           :ai-suggestions-open (richtext-ai/ai-suggestions-open? ai-state)
           :ai-toolbar-button-ref ai-toolbar-button-ref

           ;; Only used when the layout contains the :ai key
           :on-ai-click
           (fn []
             (if (richtext-ai/ai-suggestions-open? ai-state)
               (ai-dispatch (richtext-ai/close-ai-suggestions-action))
               (let [view (api/get-view api-impl)
                     _ (api/dispatch api-impl (richtext-markdown/expand-selection-for-markdown-tr (api/get-state api-impl)))
                     state (api/get-state api-impl)
                     {:keys [selection]} (bean/bean state)
                     {:keys [to]} (bean/bean selection)
                     selected-text (richtext-markdown/selected-markdown state)
                     selection-coords (.. ^js view (coordsAtPos to))
                     wrapper-rect (.. wrapper-ref -current (getBoundingClientRect))
                     width (.-width wrapper-rect)
                     ;; The PM coords are relative to the viewport
                     top (.-top selection-coords)
                     ;; Align to the left of the editor
                     left (.-left wrapper-rect)]
                 (.focus view)
                 (ai-dispatch (richtext-ai/open-ai-suggestions-action ai-context width top left selected-text)))))}])
       (when (React/isValidElement before-editor)
         before-editor)
       ;; using #js {:mount <Node>} the editor is constructed in this div,
       ;; and not appended to it.
       [:div {:ref root-el-ref
              :data-form-field-interactive true
              :style style
              :class class}]
       [(.-Portal (usePortal #js {:bindTo container-ref}))
        [:div {;; id for debugging
               :id "prosemirror-portal-ui"
               ;; re-create when the state is reset, because these
               ;; popups assign onChange events into the editor state
               ;; so they need to do it on every reset
               :key (get-editor-unique-key state)}
         [mentions/UserMentionsPopup {:view view :state state}]
         [mentions/IdeaMentionsPopup {:view view :state state}]
         [mentions/FormulaRefsMentionsPopup {:view view :state state}]]]
       (when-not (empty? toolbar-layout-bottom)
         [toolbar/HTMLEditorToolbar {:schema schema
                                     :view view
                                     :state state
                                     :layout toolbar-layout-bottom
                                     :submit-render submit-render
                                     :variant :bottom
                                     :link-editor-modal-open link-editor-modal-open
                                     :on-link-editor-modal-open #(->link-editor-modal-open true)
                                     :on-link-editor-modal-close #(->link-editor-modal-open false)}])])))

(nosco.react/forward-ref! HTMLEditorImpl)

(defnc HTMLEditorPriv [props ref]
  [richtext-ai/AISuggestionsWrapper
   [HTMLEditorImpl (assoc props
                          :ref ref)]])

(def ^{:lazy-loadable true} HTMLEditor (React/forwardRef HTMLEditorPriv))

;; TODO
;; links
;; upload files
;; embed video
;; word paste

;; Notes from redactor
;;; There's an elaborate code within redactor and further patched
;;; by Nosco to clean up pastes from Google Docs and MS word.
;;; No magic there, a lot of elbow grease. We could lift some of that
;;; off redactor, we could look for libraries or we could write similar
;;; code ourselves. Note that PM will convert all incoming HTML into
;;; conforming schema so a lot of cleanup is handled for us. Perhaps
;;; we just need to pre-process a few nodes to create e.g. lists.

;;; All the video functionality should be implemented in React and only
;;; hooked in PM via a video node. No need to look at Redactor's implementation.

;;; Same for the file uploads.

;;; Inserting links is a React UI plus inserting a link node.

;;; We can easily add new buttons (to insert e.g. reset password links etc)
;;; via the toolbar.
